import { BaseLogo, ImageRef } from "../../../../core/models/entities/BaseLogo";
import { canPreviewAsImage } from "../componenets/UploadPreview";

// "Upload stands alone" (flag REACT_APP_UPLOAD_PENDING_ENABLED, design 14/9
// §5.2) - the user's own FILE behind a worker-owned upload, and the seed the
// editor opens on. Nothing here reads the flag: the call sites only reach
// this module for a response that isUploadKindPending judged (flag-gated),
// so with the flag off none of it runs and no object URL is ever created.
//
// Why a module map: the New logo dialog (customer list / logo list), the
// empty editor and the design editor's library panel all hand the upload to
// useLogoActionsHandler.handleCreateLogo and then navigate or switch state;
// the File object and its object URL must outlive that hand-over so the
// editor can paint the file at once (raster), name it on the file card
// (vector), and "Upload another file" can offer it again. One entry per
// logo id, released when the genuine high-res lands, when the row is
// removed, or when the editor moves on.

export interface PendingUploadFile {
  file: File;
  // Object URL for browser-renderable images; null for pdf/ai/eps.
  previewUrl: string | null;
}

const pendingUploadFiles = new Map<number, PendingUploadFile>();

// Per entry: what the registrar wants done the moment the entry is dropped
// (released, evicted or replaced) - after its object URL has been revoked.
// The registrar (useLogoActionsHandler) seeded react-query's detail entry
// with that URL as the canvas bitmap, and a revoked URL left in the cache
// paints a broken image when the logo is opened again while still pending
// (refetchOnMount is off globally; skeptic round 2). The hook is where the
// cache is scrubbed - every release path, not only the editors' Cancel.
const releaseHooks = new Map<number, () => void>();

// Upper bound on entries kept at once (skeptic round 1): an editor left by
// browser-back / a route change never tells this module (a release on
// unmount is unsafe - StrictMode's simulated unmount and the per-logo
// remounts would revoke a URL the canvas still paints), so the oldest entry
// is evicted - and its object URL revoked - when a NEWER upload registers
// beyond this many. Memory is then bounded to a few files instead of one
// per upload for the rest of the session; the entry an open editor uses is
// always the newest and never evicted.
export const PENDING_UPLOAD_FILES_MAX = 3;

/** The object URL to paint `file` with, or null when an <img> cannot show it. */
export function previewUrlForFile(file: File): string | null {
  return canPreviewAsImage(file) ? URL.createObjectURL(file) : null;
}

/**
 * Remembers the file (and its object URL, whose lifetime this module now
 * owns) behind the pending upload `logoId`. An earlier entry for the same id
 * is dropped first (its URL revoked when it differs, its `onRelease` run).
 * `onRelease` runs once, right after the entry is dropped for any reason
 * (release, eviction, replacement) and after the URL is revoked.
 */
export function registerPendingUploadFile(
  logoId: number,
  file: File,
  previewUrl: string | null,
  onRelease?: () => void
): void {
  const current = pendingUploadFiles.get(logoId);
  if (current) {
    dropEntry(logoId, current, current.previewUrl !== previewUrl);
  }
  // The re-registered id becomes the NEWEST entry (Map keeps insertion
  // order; eviction below takes the oldest).
  pendingUploadFiles.set(logoId, { file, previewUrl });
  if (onRelease) releaseHooks.set(logoId, onRelease);
  while (pendingUploadFiles.size > PENDING_UPLOAD_FILES_MAX) {
    const oldest = pendingUploadFiles.keys().next().value as number | undefined;
    if (oldest === undefined) break;
    releasePendingUploadFile(oldest);
  }
}

function dropEntry(
  logoId: number,
  entry: PendingUploadFile,
  revoke: boolean
): void {
  pendingUploadFiles.delete(logoId);
  const hook = releaseHooks.get(logoId);
  releaseHooks.delete(logoId);
  if (revoke && entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
  hook?.();
}

export function getPendingUploadFile(
  logoId: number | null | undefined
): PendingUploadFile | undefined {
  if (!logoId) return undefined;
  return pendingUploadFiles.get(logoId);
}

/**
 * Forgets the entry, revokes its object URL and runs its `onRelease` hook.
 * No-op when absent.
 */
export function releasePendingUploadFile(logoId: number | null | undefined): void {
  if (!logoId) return;
  const current = pendingUploadFiles.get(logoId);
  if (!current) return;
  dropEntry(logoId, current, true);
}

/**
 * Whether `url` may still be painted for `logoId`: any URL that is not an
 * object URL (the API's own thumbnails / data-URIs), or an object URL this
 * module still holds for that logo. A blob: URL nobody holds any more has
 * been revoked - a canvas showing it is broken and must give way to
 * whatever the next detail carries (keepUploadCanvas).
 */
export function isLiveUploadPreviewUrl(
  logoId: number | null | undefined,
  url: string | null | undefined
): boolean {
  if (!url) return false;
  if (!url.startsWith("blob:")) return true;
  return getPendingUploadFile(logoId)?.previewUrl === url;
}

/** Tests only. */
export function resetPendingUploadFilesForTests(): void {
  pendingUploadFiles.forEach((entry) => {
    if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
  });
  pendingUploadFiles.clear();
  releaseHooks.clear();
  retryRequest = undefined;
  retryListeners.clear();
}

/** Tests only. */
export function pendingUploadFileCountForTests(): number {
  return pendingUploadFiles.size;
}

/**
 * The editor's seed for a pending upload answer (§5.2): raster - the user's
 * own file IS the artwork, so the object URL becomes the canvas bitmap
 * (highResUrl) with highResPending EXPLICITLY false: hasGenuineHighRes is
 * then true, so the sharp wait (skeleton) never starts, the heal never
 * polls for it, and keepSharp keeps the object URL until an ACTUAL high-res
 * arrives. lowResUrl stays exactly what the API sent - its local 180 px
 * thumbnail (the list gets it for free) or nothing (Magick failed, design
 * R4). It is never aliased to the object URL: highResUrl === lowResUrl is
 * the mapper's "no high-res" shape, and isSharpBitmap would judge the seed
 * unsharp (natural-size skeleton over a sharp file, and the thumbnail could
 * replace the file after a landing without hd - skeptic round 2). LogoImage
 * paints highResUrl on its own when useHighRes is set.
 * Vector (no preview URL) - the answer as it is: no bitmap = the file card.
 */
export function seedPendingUpload<T extends BaseLogo>(
  uploaded: T,
  previewUrl: string | null | undefined
): T {
  if (!previewUrl) return uploaded;
  const current = uploaded.displayImage ?? ({} as ImageRef);
  const displayImage: ImageRef = {
    ...current,
    lowResUrl: current.lowResUrl,
    highResUrl: previewUrl,
    highResPending: false,
  };
  return { ...uploaded, displayImage };
}

// ---------------------------------------------------------------------------
// "Upload another file" (§5.6): the rejected-upload dialog lives in the
// editor (a route of its own) or the popup, while the New logo dialog lives
// on the logo list. The request travels through this module: the caller
// records it and navigates (or is already on the list); the list's wrapper
// opens the dialog - on mount when a request is waiting, or at once through
// the listener when it is already mounted - and the dialog component
// consumes the request in its first render (customer preselected, the file
// pre-filled when the rejection allows it).

export interface UploadRetryRequest {
  customerId?: number;
  file?: File;
}

// A request lives this long (skeptic round 1): it is recorded right before
// the navigation to the logo list, which consumes it on mount. Should the
// list not mount (another route, the user navigates elsewhere first), the
// request must not lie in wait and open the New logo dialog - with a
// pre-filled file - on an unrelated visit minutes later. Generous enough for
// a slow route change, far too short to survive a detour.
//
// The dialog reads the request in its first render (useState initializer)
// and drops it in an effect (skeptic round 2): React's StrictMode runs a
// mount's initializers twice and keeps the second result, so a consume-on-
// read would hand the request to the discarded first run and open the
// dialog on today's first step without customer or file in local dev
// (index.tsx wraps the app in <React.StrictMode>; production builds render
// once). Peek + clear-in-effect is idempotent per mount.
export const UPLOAD_RETRY_REQUEST_TTL_MS = 30_000;

let retryRequest:
  | { request: UploadRetryRequest; requestedAt: number }
  | undefined;
const retryListeners = new Set<() => void>();

export function requestUploadRetry(
  request: UploadRetryRequest,
  now: number = Date.now()
): void {
  retryRequest = { request, requestedAt: now };
  retryListeners.forEach((listener) => listener());
}

// The waiting request, or undefined when none waits or it has expired (an
// expired one is dropped on the spot - never handed over later).
function liveRetryRequest(now: number): UploadRetryRequest | undefined {
  if (!retryRequest) return undefined;
  if (now - retryRequest.requestedAt > UPLOAD_RETRY_REQUEST_TTL_MS) {
    retryRequest = undefined;
    return undefined;
  }
  return retryRequest.request;
}

export function hasUploadRetryRequest(now: number = Date.now()): boolean {
  return liveRetryRequest(now) !== undefined;
}

/**
 * The waiting request WITHOUT dropping it - undefined when none waits (an
 * expired one is dropped). Pair with clearUploadRetryRequest once the reader
 * has settled on it.
 */
export function peekUploadRetryRequest(
  now: number = Date.now()
): UploadRetryRequest | undefined {
  return liveRetryRequest(now);
}

/** Hands the waiting request over (once) - undefined when none is waiting. */
export function consumeUploadRetryRequest(
  now: number = Date.now()
): UploadRetryRequest | undefined {
  const request = liveRetryRequest(now);
  retryRequest = undefined;
  return request;
}

/** Drops a waiting request without handing it over (no-op when none). */
export function clearUploadRetryRequest(): void {
  retryRequest = undefined;
}

/** Subscribes to new requests; returns the unsubscribe. */
export function onUploadRetryRequested(listener: () => void): () => void {
  retryListeners.add(listener);
  return () => {
    retryListeners.delete(listener);
  };
}
